/*
 * Copyright (c) 2023 Bluemate B.V.
 */
import './style/adminUsers.scss';

import React, {KeyboardEventHandler, MouseEventHandler, useContext} from 'react';
import {useTranslation} from 'react-i18next';

import User from '../../../models/userModel';

import {
    AwesomeIcon,
    ChangeFunction,
    ElementPosition,
    Loader,
    MenuPlacement,
    OptionsMenu,
    OptionsMenuItem
} from '@dvrd/dvr-controls';

import classNames from 'classnames';

import {USERS_LIMIT} from './adminUsersController';

import {AppContext} from '../../misc/appContext';

import {SubscriptionStats, SubscriptionStatus, UserStatus} from '../../../util/interfaces';

import {BluemateButton, BluemateInput} from '../../controls';

import {getSubscriptionLabel, inactiveSubs} from '../../../util/subscriptionUtil';

import {Icon} from '@iconify/react';

import optionsHorizontal from '@iconify/icons-mi/options-horizontal';

interface Props {
    onChangeSearch: ChangeFunction<string>;
    onClickEdit: (user: User) => MouseEventHandler;
    onChangePage: (page: number) => MouseEventHandler;
    onLoginUser: (user: User) => MouseEventHandler;
    onSearch: KeyboardEventHandler;
    onSubStats: MouseEventHandler;
    onCopyId: (user: User) => MouseEventHandler;
    onOpenFilter: MouseEventHandler;
    onResetFilter: MouseEventHandler;
    onClickChurn: MouseEventHandler;
    users: User[];
    loading: boolean;
    page: number;
    search: string;
    total: number;
    subStats: SubscriptionStats;
    hasActiveFilter: boolean;
}

export default function AdminUsers(props: Props) {
    const {t} = useTranslation();
    const {
        page,
        onChangePage,
        onChangeSearch,
        loading,
        onSearch,
        search,
        users,
        onLoginUser,
        total,
        onSubStats,
        subStats,
        onClickEdit,
        onCopyId,
        onOpenFilter,
        onResetFilter,
        onClickChurn,
        hasActiveFilter
    } = props;
    const context = useContext(AppContext);

    function onClickBack() {
        history.back();
    }

    function getOptions(user: User, showLogin: boolean): OptionsMenuItem[] {
        const items: OptionsMenuItem[] = [
            {
                label: t('admin.users.detailsAction', 'Details'),
                onClick: onClickEdit(user),
                icon: <AwesomeIcon name='eye' className='icon' />
            },
            {
                label: t('admin.users.copyId', 'ID kopiëren'),
                onClick: onCopyId(user),
                icon: <AwesomeIcon name='clipboard' className='icon' />
            }
        ];
        if (showLogin)
            items.push({
                label: t('admin.users.login', 'Inloggen'),
                onClick: onLoginUser(user),
                icon: <AwesomeIcon name='sign-in-alt' className='icon' />
            });
        return items;
    }

    function renderHeader() {
        return (
            <div className='header-container'>
                <BluemateButton onClick={onClickBack} label={t('admin.users.back', 'Terug')} disabled={loading} />
                <div className='title-header-container'>
                    <div className='title-container'>
                        <label className='header-title'>{t('admin.users.title', 'Gebruikers')}</label>
                        <BluemateInput
                            value={search}
                            onChange={onChangeSearch}
                            label={t('admin.users.search', 'Zoeken')}
                            ornaments={{
                                element: <AwesomeIcon name='search' />,
                                placement: ElementPosition.RIGHT
                            }}
                            className='search-field'
                            onEnter={onSearch}
                            autoFocus
                            unControlled
                        />
                        <BluemateButton
                            onClick={onOpenFilter}
                            label={t('admin.users.filterAction', 'Filter')}
                            secondary
                        />
                        {hasActiveFilter && (
                            <BluemateButton
                                onClick={onResetFilter}
                                label={t('admin.users.clearFilters', 'Filters wissen')}
                                secondary
                                className='reset-filter-btn'
                            />
                        )}
                    </div>
                    {renderSubStats()}
                </div>
            </div>
        );
    }

    function renderSubStats() {
        return (
            <div className='sub-stats'>
                <div className='stats-row'>
                    <label className='stat green'>
                        {t('admin.users.stats.active', 'Actief')}: {subStats.active}
                    </label>
                    <label className='stat yellow'>
                        {t('admin.users.stats.trial', 'Proefperiode')}: {subStats.trialing}
                    </label>
                    <label className='stat blue'>
                        {t('admin.users.stats.free', 'Gratis')}: {subStats.free}
                    </label>
                    <label className='stat red'>
                        {t('admin.users.stats.inactive', 'Inactief')}:{' '}
                        {subStats.canceled + subStats.expired + subStats.inactive}
                    </label>
                </div>
                <div className='actions-row'>
                    <BluemateButton onClick={onSubStats} label={t('admin.users.detailsLabel', 'Details')} />
                    <BluemateButton onClick={onClickChurn} label={t('admin.users.churn', 'Abonnementen verloop')} />
                </div>
            </div>
        );
    }

    function renderBody() {
        return (
            <div className='body-container'>
                <div className='row head'>
                    <label>{t('admin.users.col.createdAt', 'AANGEMAAKT OP')}</label>
                    <label>{t('admin.users.col.name', 'NAAM')}</label>
                    <label>{t('admin.users.col.subscription', 'ABONNEMENT')}</label>
                    <label>{t('admin.users.col.email', 'E-MAILADRES')}</label>
                    <label>{t('admin.users.col.lastLogin', 'LAATSTE LOGIN')}</label>
                    <div />
                </div>
                <div className='table-body'>
                    <Loader active={loading} />
                    {users.map(renderUserRow)}
                    {renderEmptyRows()}
                </div>
            </div>
        );
    }

    function getSubLabel(user: User): [string, string] {
        if (!user.subscription) return [t('admin.users.extraUser', 'Extra gebruiker'), 'blue'];
        const subStatus = user.subscription?.status ?? SubscriptionStatus.INACTIVE;
        const subscriptionColor = inactiveSubs.includes(subStatus)
            ? 'red'
            : subStatus === SubscriptionStatus.TRIAL
              ? 'yellow'
              : subStatus === SubscriptionStatus.FREE
                ? 'blue'
                : 'green';
        return [getSubscriptionLabel(subStatus), subscriptionColor];
    }

    function renderUserRow(user: User) {
        const adminUser = context.userContext.user;
        const showLogin = (adminUser?.isSuperAdmin() && user.id !== adminUser.id) ?? false;
        const [subLabel, subColor] = getSubLabel(user);
        const optionsMenu = React.createRef<OptionsMenu>();

        function onClick() {
            optionsMenu.current?.onOpenExternal();
        }

        return (
            <div
                className='row'
                key={user.id}
                onDoubleClick={showLogin ? onLoginUser(user) : undefined}
                onClick={onClick}
            >
                <label>{user.createdAtMoment.format('DD-MM-YYYY HH:mm')}</label>
                <label title={user.fullName}>
                    {renderUserStatus(user)}
                    {user.fullName}
                </label>
                <label className={subColor}>{subLabel}</label>
                <label title={user.email}>{user.email}</label>
                <label>
                    {user.lastLogin ? user.lastLoginMoment.format('DD-MM-YYYY HH:mm') : t('admin.users.na', 'n.v.t.')}
                </label>
                <OptionsMenu
                    options={getOptions(user, showLogin)}
                    placement={MenuPlacement.BOTTOM_LEFT}
                    optionsComponent={<Icon icon={optionsHorizontal} className='options-icon' />}
                    containerClass='common-options'
                    ref={optionsMenu}
                />
            </div>
        );
    }

    function renderUserStatus(user: User) {
        if (user.status === UserStatus.ACTIVE) return null;
        return (
            <>
                <AwesomeIcon name='lock' className='user-lock' title={t('admin.users.blocked', 'Geblokkeerd')} />
                &nbsp;
            </>
        );
    }

    function renderEmptyRows() {
        const emptyRows = USERS_LIMIT - users.length;
        if (emptyRows <= 0) return null;
        const rows: React.ReactElement[] = [];
        for (let i = 0; i < emptyRows; i++)
            rows.push(
                <div className='row empty' key={i}>
                    <label>{t('admin.users.placeholder.createdAt', 'Aangemaakt op')}</label>
                    <label>{t('admin.users.placeholder.name', 'Naam')}</label>
                    <label>{t('admin.users.placeholder.companyName', 'Bedrijfsnaam')}</label>
                    <label>{t('admin.users.placeholder.email', 'E-mailadres')}</label>
                    <label>{t('admin.users.placeholder.lastLogin', 'Laatste login')}</label>
                    <label>{t('admin.users.placeholder.status', 'Status')}</label>
                    <div />
                </div>
            );
        return rows;
    }

    function renderFooter() {
        const maxPage = Math.ceil(total / USERS_LIMIT);
        const prevDisabled = page <= 0;
        const nextDisabled = page >= maxPage - 1;
        const start = Math.min(total, Math.max(1, page * USERS_LIMIT));
        const end = Math.min(total, (page + 1) * USERS_LIMIT);
        return (
            <div className='footer-container'>
                <label className='count-label'>
                    {start} {t('admin.users.pagingTo', 't/m')} {end} {t('admin.users.pagingOf', 'van')} {total}
                </label>
                <div className='pagination-container'>
                    <AwesomeIcon
                        name='chevron-left'
                        className={classNames('pager', prevDisabled && 'disabled')}
                        onClick={prevDisabled ? undefined : onChangePage(page - 1)}
                    />
                    <AwesomeIcon
                        name='chevron-right'
                        className={classNames('pager', nextDisabled && 'disabled')}
                        onClick={nextDisabled ? undefined : onChangePage(page + 1)}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className='admin-users'>
            {renderHeader()}
            {renderBody()}
            {renderFooter()}
        </div>
    );
}
